import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    EquipmentModel,
    isDefined,
    LoadingScreenComponent,
    LoadingScreenModel,
    NotificationModel,
    SoftwareLicenceModel,
    SoftwareLicencesService,
    TableModel,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import EquipmentConfigurationModel from './equipment-configuration.model';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-equipment-configuration',
    templateUrl: './equipment-configuration.component.html',
    styleUrls: ['./equipment-configuration.component.scss'],
})
export class EquipmentConfigurationComponent implements OnInit {
    @Input()
    public model: EquipmentConfigurationModel;

    @Input()
    public notification: NotificationModel;

    public softwareLicences$: Observable<TableModel<SoftwareLicenceModel>>;

    public equipmentConfigurationForm: FormGroup;
    public equipmentConfigurationFormChildren: FormArray;

    public readonly technicalIdOrFunctionalLocationLabel: string;
    public readonly allEquipmentsLabel: string;
    public readonly withServiceKeyLabel: string;
    public readonly softwareLicencesLabel: string;
    public readonly dateFormat: string;

    public displayContractField = true;
    public displayMaparField = true;
    public serviceKeyTabAsDefault = true;
    public softwareLicencesDisplayed = false;
    public timeFormat: string;
    public enableSoftwareLicences = false;
    public orderByField: string;
    public reverseSort: boolean;
    public faSortDown = faSortDown;
    public faSortUp = faSortUp;
    public constructor(
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        public readonly modalInstance: BsModalRef,
        private readonly appNotificationService: AppNotificationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly softwareLicencesService: SoftwareLicencesService,
        public readonly changeDetector: ChangeDetectorRef,
    ) {
        this.timeFormat = this.clientManager.getDateFormat();
        this.technicalIdOrFunctionalLocationLabel =
            this.clientManager.isNgmAP0() || this.clientManager.isPaceUS()
                ? <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_FLNR')
                : <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_TIDNR');

        this.allEquipmentsLabel = <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_ALL');
        this.withServiceKeyLabel = <string>(
            this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_WITH_SERVICE_KEY')
        );

        this.enableSoftwareLicences = this.clientManager.isNgmP58() || this.clientManager.isPaceUS();

        this.softwareLicencesLabel = <string>(
            this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_SOFTWARE_LICENCES')
        );
        this.dateFormat = this.clientManager.getDateFormat();

        if (this.clientManager.isPaceUS()) {
            this.displayContractField = false;
            this.displayMaparField = false;
            this.serviceKeyTabAsDefault = false;
        }
    }

    public ngOnInit(): void {
        if (typeof this.model.tableModel.selectedRow === 'undefined' && this.model.tableModel.rows.length > 0) {
            this.model.tableModel.selectByIndex(0);
        }

        this.model.updateServiceKeyValue();
        this.equipmentConfigurationFormChildren = this.formBuilder.array([]);
        this.equipmentConfigurationForm = this.formBuilder.group({
            children: this.equipmentConfigurationFormChildren,
        });

        this.orderByField = this.model.tableModel.options.sortColumn;
        this.reverseSort = this.model.tableModel.options.reverseSort;

        if (this.enableSoftwareLicences) {
            this.updateSoftwareLicences();
        }
    }

    public switchTabs(index: number): void {
        this.softwareLicencesDisplayed = false;
        this.model.showModals(index);
    }

    /**
     * This function shows the popup with complete service key value
     */
    public showServiceKey(): void {
        this.appNotificationService.setAppNotification(
            AppNotificationType.INFO,
            <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_SERVICE_KEY'),
            EquipmentModel.returnFormattedServiceKey(this.model.tableModel.selectedRow.serviceKey.value),
        );
    }

    public displaySoftwareLicences(): void {
        this.softwareLicencesDisplayed = true;
    }

    public updateSoftwareLicences(): void {
        const loadingScreenModel = new LoadingScreenModel(
            <string>this.translate.instant('EQUIPMENT_CONFIGURATION_SOFTWARE_LICENCES_LOADING'),
        );
        const loadingModalInstance = this.modalService.show(LoadingScreenComponent, {
            class: 'modal-sm modal-fullscreen-sm-down',
            initialState: {
                model: loadingScreenModel,
            },
        });

        const mainEquipment = this.notification.getMainEquipment();
        if (!isDefined(mainEquipment)) {
            throw new Error(`EquipmentConfigurationComponent: updateSoftwareLicences: No main Equipment available!`);
        }
        this.softwareLicences$ = this.softwareLicencesService
            .getTableModelForSoftwareLicences(mainEquipment)
            .pipe(finalize(() => loadingModalInstance.hide()));
    }

    public doSort(field: string): void {
        this.orderByField = field;
        this.reverseSort = !this.reverseSort;
    }
}
